/**
 * Armado de la grilla de un mes. **Sin React y sin red: solo fechas.**
 *
 * Vive aparte de `CalendarView.tsx` porque es lo unico del calendario que tiene
 * logica de verdad —cuantas semanas entran, que dias son del mes de al lado,
 * donde cae cada vencimiento— y eso se prueba con una tabla de casos, no
 * abriendo el navegador y contando cuadraditos.
 *
 * LAS FECHAS SE TRATAN COMO TEXTO `YYYY-MM-DD`, NUNCA COMO `Date`.
 * =================================================================
 * Es la decision central del archivo y hay un motivo medido. `end_date` es un
 * `DateField` de Django: viaja como `"2026-08-31"`, sin hora y sin zona. En
 * cuanto se hace `new Date("2026-08-31")` el navegador lo interpreta como
 * medianoche UTC, y en la Argentina (UTC-3) eso es **el 30 a las 21:00**. O sea
 * que el vencimiento del ultimo dia del mes se dibuja en el dia anterior, y el
 * del dia 1 se va al mes pasado y desaparece de la grilla.
 *
 * Por eso todo lo que compara o agrupa fechas de datos usa cadenas
 * (`"2026-08-31" === "2026-08-31"`), que es exacto y no depende de donde este
 * parado el navegador. `Date` se usa SOLO para el almanaque —saber que dia de la
 * semana cae el 1, cuantos dias tiene el mes— y ahi se lo construye siempre con
 * el constructor de componentes locales (`new Date(anio, mes, dia)`), que no
 * pasa por UTC.
 *
 * La semana empieza el LUNES, que es como se lee un almanaque en la Argentina.
 */

/** Un mes concreto. `mes` es 1-12, no 0-11: es lo que se lee en la URL. */
export interface MesDelCalendario {
  anio: number;
  mes: number;
}

/** Una celda de la grilla: un dia, con lo que cae ese dia. */
export interface DiaDelCalendario<T> {
  /** `YYYY-MM-DD`. Es la clave con la que se agrupa y se compara. */
  fecha: string;
  /** Numero del dia (1-31), para dibujarlo. */
  numero: number;
  /** `false` en los dias del mes anterior o del siguiente que rellenan la grilla. */
  delMes: boolean;
  items: T[];
}

/** Rotulos de los siete dias, empezando el lunes. */
export const DIAS_DE_LA_SEMANA = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'] as const;

const NOMBRES_DE_MES = [
  'enero',
  'febrero',
  'marzo',
  'abril',
  'mayo',
  'junio',
  'julio',
  'agosto',
  'septiembre',
  'octubre',
  'noviembre',
  'diciembre',
] as const;

/** `"2026-08-18"` de un mes y un dia, sin pasar por `Date`. */
export function claveDeFecha(anio: number, mes: number, dia: number): string {
  return `${String(anio).padStart(4, '0')}-${String(mes).padStart(2, '0')}-${String(dia).padStart(2, '0')}`;
}

/** El dia de hoy en la zona del navegador, como `YYYY-MM-DD`. */
export function hoyIso(ahora: Date = new Date()): string {
  return claveDeFecha(ahora.getFullYear(), ahora.getMonth() + 1, ahora.getDate());
}

/** El mes al que pertenece una fecha `YYYY-MM-DD`, o `null` si no es una. */
export function mesDeLaFecha(fecha: string): MesDelCalendario | null {
  const partes = /^(\d{4})-(\d{2})-(\d{2})$/.exec(fecha);
  if (partes === null) return null;
  const anio = Number(partes[1]);
  const mes = Number(partes[2]);
  if (mes < 1 || mes > 12) return null;
  return { anio, mes };
}

/** El mes de hoy. Es donde abre el calendario cuando la URL no dice otra cosa. */
export function mesActual(ahora: Date = new Date()): MesDelCalendario {
  return { anio: ahora.getFullYear(), mes: ahora.getMonth() + 1 };
}

/** Corre `cuantos` meses (puede ser negativo) sin desbordar el numero de mes. */
export function correrMes(mes: MesDelCalendario, cuantos: number): MesDelCalendario {
  // Se cuenta en meses absolutos desde el año 0 y se vuelve a partir: asi
  // «diciembre + 1» da enero del año siguiente y «enero - 1» diciembre del
  // anterior, sin un solo `if`.
  const absoluto = mes.anio * 12 + (mes.mes - 1) + cuantos;
  return { anio: Math.floor(absoluto / 12), mes: (absoluto % 12) + 1 };
}

/** «agosto de 2026». En minuscula: se compone dentro de una frase. */
export function nombreDelMes(mes: MesDelCalendario): string {
  return `${NOMBRES_DE_MES[mes.mes - 1] ?? ''} de ${mes.anio}`;
}

/** Cuantos dias tiene el mes. El dia 0 del mes siguiente es el ultimo de este. */
export function diasDelMes(mes: MesDelCalendario): number {
  return new Date(mes.anio, mes.mes, 0).getDate();
}

/**
 * Rango `[desde, hasta]` del mes, en `YYYY-MM-DD` y **ambos inclusive**.
 *
 * Es lo que se le manda al servidor como dominio (`>=` desde, `<=` hasta). Se
 * pide el mes exacto y no la grilla completa a proposito: los dias de relleno
 * son del mes de al lado y se ven en su propia pagina; traerlos duplicaria filas
 * entre dos meses y haria que el total del pie no cierre con lo que se ve.
 */
export function rangoDelMes(mes: MesDelCalendario): { desde: string; hasta: string } {
  return {
    desde: claveDeFecha(mes.anio, mes.mes, 1),
    hasta: claveDeFecha(mes.anio, mes.mes, diasDelMes(mes)),
  };
}

/**
 * Cuantos dias del mes ANTERIOR hay que poner adelante para que el 1 caiga en su
 * columna. `getDay()` devuelve 0 para domingo; con la semana arrancando en lunes,
 * el domingo es la septima columna.
 */
function relleno(mes: MesDelCalendario): number {
  const diaDeLaSemana = new Date(mes.anio, mes.mes - 1, 1).getDay();
  return (diaDeLaSemana + 6) % 7;
}

/**
 * La grilla del mes: filas de siete dias, con los items ya repartidos.
 *
 * @param mes el mes a dibujar.
 * @param items lo que hay que ubicar.
 * @param fechaDe como se saca la fecha `YYYY-MM-DD` de cada item. Un item cuya
 *   fecha no sea una fecha valida se descarta en silencio: `end_date` es
 *   nullable —hay ventas que no vencen— y una venta sin vencimiento no tiene un
 *   dia en el que dibujarse.
 * @returns siempre semanas completas de 7 dias. La ultima puede tener dias del
 *   mes siguiente; los de relleno vienen con `delMes: false` para que la
 *   pantalla los apague.
 */
export function armarGrilla<T>(
  mes: MesDelCalendario,
  items: readonly T[],
  fechaDe: (item: T) => string,
): DiaDelCalendario<T>[][] {
  const porFecha = new Map<string, T[]>();
  for (const item of items) {
    const fecha = fechaDe(item);
    if (mesDeLaFecha(fecha) === null) continue;
    const cubo = porFecha.get(fecha);
    if (cubo === undefined) porFecha.set(fecha, [item]);
    else cubo.push(item);
  }

  const antes = relleno(mes);
  const cuantos = diasDelMes(mes);
  // Semanas completas: el techo de (relleno + dias) sobre 7. Un mes de 28 dias
  // que arranca lunes ocupa 4 filas; uno de 31 que arranca domingo, 6.
  const semanas = Math.ceil((antes + cuantos) / 7);

  const anterior = correrMes(mes, -1);
  const siguiente = correrMes(mes, 1);
  const diasAntes = diasDelMes(anterior);

  const grilla: DiaDelCalendario<T>[][] = [];
  for (let semana = 0; semana < semanas; semana += 1) {
    const fila: DiaDelCalendario<T>[] = [];
    for (let columna = 0; columna < 7; columna += 1) {
      const posicion = semana * 7 + columna - antes;
      let fecha: string;
      let numero: number;
      let delMes: boolean;

      if (posicion < 0) {
        numero = diasAntes + posicion + 1;
        fecha = claveDeFecha(anterior.anio, anterior.mes, numero);
        delMes = false;
      } else if (posicion >= cuantos) {
        numero = posicion - cuantos + 1;
        fecha = claveDeFecha(siguiente.anio, siguiente.mes, numero);
        delMes = false;
      } else {
        numero = posicion + 1;
        fecha = claveDeFecha(mes.anio, mes.mes, numero);
        delMes = true;
      }

      fila.push({ fecha, numero, delMes, items: porFecha.get(fecha) ?? [] });
    }
    grilla.push(fila);
  }
  return grilla;
}

/**
 * Suma importes que llegan como STRING, **sin pasarlos por `float`**.
 *
 * Es la regla 4 del proyecto y no es una formalidad: el backend serializa los
 * `Decimal` como texto justamente para no perder precisión, y `Number()` la
 * pierde. Acá se suma en centavos con enteros (`BigInt`) y se devuelve el total
 * como string, que es lo que `formatCurrency` sabe recibir.
 *
 * Vive con el resto de la lógica del calendario, y no adentro del componente,
 * por lo mismo que el armado de la grilla: es lo único del pie del mes que puede
 * estar mal sin que se note, así que se prueba con una tabla de casos.
 *
 * Lo que no tenga forma de importe se ignora en vez de contarse como cero
 * silencioso. La cuenta puede quedar corta —y se ve, porque falta una fila— pero
 * nunca mal.
 */
export function sumarImportes(valores: readonly string[]): string {
  let centavos = 0n;
  for (const crudo of valores) {
    const partes = /^(-?)(\d+)(?:[.](\d{1,2}))?$/.exec(crudo.trim());
    if (partes === null) continue;
    const decimales = (partes[3] ?? '').padEnd(2, '0');
    const valor = BigInt(partes[2]!) * 100n + BigInt(decimales);
    centavos += partes[1] === '-' ? -valor : valor;
  }
  const negativo = centavos < 0n;
  const absoluto = negativo ? -centavos : centavos;
  return `${negativo ? '-' : ''}${absoluto / 100n}.${String(absoluto % 100n).padStart(2, '0')}`;
}
